import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { formatDate } from 'utils/formatter';
import {
  Group,
  isCategorized,
  getCategorizedGroups,
  TargetGoal,
} from 'hooks/useGrouping';
import GroupDeleteConfirmModal, {
  GROUP_DELETE_CONFIRM_MODAL_TYPE,
} from '../modal/GroupDeleteConfirmModal';

type GroupOperationProps = {
  currentGroup: Group;
  isTabChanged: boolean;
  editGroupName: (newName: string) => void;
  deleteGroup: () => void;
  groups: Group[];
};

const GroupOperation: React.FC<GroupOperationProps> = props => {
  const [nameInput, setNameInput] = useState<
    | { isEdit: false }
    | {
        isEdit: true;
        newName: string;
        isValid: boolean;
        errorMessages: string[];
      }
  >({ isEdit: false });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(
    false
  );
  const focusNameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!nameInput.isEdit) return;
    if (!focusNameInputRef.current) return;
    focusNameInputRef.current.focus();
  }, [nameInput.isEdit]);

  const closeModal = useCallback((): void => {
    setShowDeleteConfirmModal(false);
  }, []);

  const showModal = (): void => {
    setShowDeleteConfirmModal(true);
  };

  useEffect(() => {
    // タブが変更されたらモーダルを閉じる
    if (props.isTabChanged) closeModal();
  }, [props.isTabChanged, closeModal]);

  useEffect(() => {
    // タブが変更されたら編集モードを終了する
    if (props.isTabChanged)
      setNameInput({
        isEdit: false,
      });
  }, [props.isTabChanged]);

  const startEditing = useCallback((): void => {
    setNameInput({
      isEdit: true,
      newName: props.currentGroup.name,
      isValid: true,
      errorMessages: [],
    });
  }, [props.currentGroup.name]);

  if (!isCategorized(props.currentGroup)) return null;

  const validate = (newName: string): [boolean, string[]] => {
    const errorMessages = [];
    const isValids = [];
    if (
      props.groups
        .filter(group => group.id.id !== props.currentGroup.id.id)
        .map(group => group.name)
        .includes(newName)
    ) {
      isValids.push(false);
      errorMessages.push('既に存在するグループ名での登録は出来ません。');
    }
    if (newName === '') {
      isValids.push(false);
      errorMessages.push('グループ名を入力してください。');
    }
    return [isValids.every(b => b), errorMessages];
  };

  const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;
    setNameInput(({ isEdit }) => {
      if (isEdit) {
        const [isValid, errorMessages] = validate(value);
        return {
          isEdit: true,
          newName: value,
          isValid: isValid,
          errorMessages: errorMessages,
        };
      }
      return { isEdit: false };
    });
  };

  const blurHandler = (): void => {
    if (!nameInput.isEdit) return;
    if (!nameInput.isValid) return;
    props.editGroupName(nameInput.newName);
    setNameInput({ isEdit: false });
  };

  const onModalDeleteHandler = (): void => {
    props.deleteGroup();
    closeModal();
  };

  const modalOption = (():
    | {
        type: 0;
        targetGoal?: undefined;
        completeGoal?: undefined;
      }
    | {
        type: 1;
        targetGoal: TargetGoal;
        completeGoal?: undefined;
      }
    | {
        type: 2;
        targetGoal: TargetGoal;
      } => {
    if (!props.currentGroup.targetGoal)
      return { type: GROUP_DELETE_CONFIRM_MODAL_TYPE.紐付けなし };
    const { targetGoal } = props.currentGroup;
    const targetGoalIds = (getCategorizedGroups(props.groups)
      .map(group => group.targetGoal)
      .filter(t => Boolean(t)) as TargetGoal[]).map(t => t.id);
    if (targetGoalIds.filter(id => id === targetGoal.id).length > 1)
      return {
        type: GROUP_DELETE_CONFIRM_MODAL_TYPE.紐付けありかつ目標削除なし,
        targetGoal: props.currentGroup.targetGoal,
      };
    return {
      type: GROUP_DELETE_CONFIRM_MODAL_TYPE.紐付けありかつ目標削除あり,
      targetGoal: props.currentGroup.targetGoal,
    };
  })();

  return (
    <>
      <div className="grouping-tab-body-header">
        <div className="grouping-tab-body-header-primary-label">
          <div className="icon-folder-outline group-badge"></div>
          {!nameInput.isEdit && (
            <span className="grouping-tab-body-name">
              {props.currentGroup.name}
            </span>
          )}
          {nameInput.isEdit && (
            <>
              <input
                value={nameInput.newName}
                onChange={changeHandler}
                onBlur={blurHandler}
                className="grouping-tab-body-input"
                ref={focusNameInputRef}
              />
              {nameInput.errorMessages.length ? (
                <ul className="grouping-tab-body-errors">
                  {nameInput.errorMessages.map(m => (
                    <li key={m} className="form-error-note">
                      <div className="icon-alert"></div>
                      {m}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>
        <div className="grouping-tab-body-header-secondary-label">
          {`${formatDate(
            props.currentGroup.createdAt,
            'yyyy/MM/dd HH:mm'
          )} 作成`}
        </div>
        <ul className="grouping-tab-control-list">
          <li
            className="grouping-tab-control-list-item cursor-pointer"
            onClick={startEditing}>
            <i className="icon-edit"></i>グループ名を編集
          </li>
          <li
            className="grouping-tab-control-list-item cursor-pointer"
            onClick={showModal}>
            <i className="icon-trash"></i>グループを削除
          </li>
          <GroupDeleteConfirmModal
            isShow={showDeleteConfirmModal}
            close={closeModal}
            delete={onModalDeleteHandler}
            {...modalOption}
          />
        </ul>
      </div>
    </>
  );
};

export default GroupOperation;
